import type { ColumnDef, TableMeta, SortingState, Row, Table, Cell } from '@tanstack/react-table';
import { IndeterminateCheckbox } from 'components/inputs/IndeterminateCheckbox';
import { TableFilter, TableMetaProps, TableState } from 'features/misc/table/types';
import type { MessageDescriptor } from '@lingui/core';
import { TypeId } from '../components/Table';
import { ReactNode } from 'react';
import { i18n } from '@lingui/core';
import { CaretDown, CaretRight } from '@phosphor-icons/react';
import { Box } from '@mui/material';
import { colors } from 'styles/theme/colors';

export const createFillerColumn = <T,>(minCellWidth: number): ColumnDef<T> => {
  return {
    id: 'Filler',
    accessorKey: 'filler',
    header: '',
    size: minCellWidth,
  };
};

export const getTableMeta = <T,>(
  setData: React.Dispatch<React.SetStateAction<T[]>>,
  metaProps?: TableMetaProps<T>,
  selectedRows?: number[]
) => {
  return {
    updateData: (rowIndex: number, tableUpdate: Record<string, unknown>) => {
      setData(old =>
        old.map((row, index) => {
          if (index !== rowIndex) return row;

          return {
            ...old[rowIndex],
            ...tableUpdate,
          };
        })
      );
    },
    ...(metaProps ? metaProps : {}),
    ...(metaProps?.updateData && selectedRows
      ? {
          updateData: (rowIndex, tableUpdate, apiUpdate, isLinked) =>
            metaProps.updateData?.(selectedRows, rowIndex, tableUpdate, apiUpdate, isLinked),
        }
      : {}),
  } as TableMeta<T>;
};

export const renderIndentation = (depth: number) =>
  Array(depth)
    .fill(null)
    .map((_, i) => (
      <Box
        key={`indent-${i + 1}`}
        width="72px"
        display="flex"
        alignItems="center"
        padding="8px 24px"
        height="100%"
      />
    ));

export const createSelectorColumn = <T extends TypeId>(
  isSelectable?: boolean,
  isExpandable?: boolean,
  subRowsDepth = 1
): ColumnDef<T>[] => {
  if (!isExpandable && !isSelectable) {
    return [];
  }
  const checkboxCell = (row: Row<T>) => (
    <Box padding="8px 15px" display="flex" alignItems="center" height="100%">
      <IndeterminateCheckbox
        disabled={row.original.id === -1}
        checked={row?.getIsSelected()}
        indeterminate={row.getIsSomeSelected()}
        onChange={row.getToggleSelectedHandler()}
      />
    </Box>
  );

  const renderToggleIcon = (row: Row<T>) => {
    const Icon = row.getIsExpanded() ? CaretDown : CaretRight;
    return (
      <Box padding="8px 24px" display="flex" alignItems="center">
        <Icon
          size={16}
          color={colors.grey[700]}
          onClick={row.getToggleExpandedHandler()}
          style={{ cursor: 'pointer', minWidth: '16px' }}
        />
      </Box>
    );
  };

  const depth = isSelectable ? subRowsDepth : subRowsDepth - 1 || 1;

  return [
    {
      id: 'Selector',
      enableSorting: false,
      maxSize: 72 * depth,
      minSize: 72 * depth,
      size: 72 * depth,
      enableResizing: false,
      header: ({ table }) =>
        isSelectable ? (
          <IndeterminateCheckbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ) : (
          ''
        ),
      cell: ({ row }) => {
        if (row.original.id === -1) return null;
        if (isExpandable) {
          return (
            <Box
              display="flex"
              alignItems="center"
              height="100%"
              sx={{
                borderRight: `0.5px solid ${colors.grey[200]}`,
              }}
            >
              {renderIndentation(row.depth ?? 0)}
              {isSelectable && checkboxCell(row)}
              {row.getCanExpand() ? renderToggleIcon(row) : null}
            </Box>
          );
        }
        return checkboxCell(row);
      },
    },
  ];
};

export const updateSorting = (sortingState: SortingState, tableState?: TableState) => {
  const oldSort = tableState?.sort ?? null;
  const oldOrder = tableState?.order;

  const newSort = sortingState[0]?.id ?? oldSort;
  const isSameSort = newSort === oldSort;

  const order = isSameSort
    ? oldOrder === 'ASC'
      ? 'DESC'
      : oldOrder === 'DESC'
        ? null
        : 'ASC'
    : 'ASC';

  const sort = newSort && order ? newSort : null;

  return { sort, order: sort ? order : null };
};

export const rowHeight = 64;

export const trans = (header: string | ReactNode | MessageDescriptor): string | ReactNode => {
  // lingui message id
  if (typeof header === 'string') {
    return i18n._(header);
  }
  // lingui message descriptors are objects with an 'id' property
  if (typeof header === 'object' && header !== null && 'id' in header) {
    return i18n._(header as MessageDescriptor);
  }
  return header;
};

export const getIsFilterChange = (
  obj1: Record<string, string | null>,
  obj2: Record<string, string | null>,
  allFilters: TableFilter[]
): boolean => {
  for (const key in obj1) {
    const isKeyInFilter = allFilters.some(item => item?.id === key);
    const val1 = obj1[key];
    const val2 = obj2[key];

    if (isKeyInFilter && String(val1) !== String(val2)) {
      return true;
    }
  }
  return false;
};

export function getTotalHeadersSize<T>(table?: Table<T>): number {
  if (!table) return 0;
  return table?.getHeaderGroups()?.reduce((total, headerGroup) => {
    return (
      total +
      headerGroup?.headers?.reduce((groupTotal, header) => {
        const isFrozen = header.column.getIsPinned();
        if (isFrozen) {
          return groupTotal + 0;
        }
        return groupTotal + header?.getSize();
      }, 0)
    );
  }, 0);
}

export function isCellEditable<T>(
  editable?: boolean | ((value?: Cell<T, unknown>) => boolean),
  cell?: Cell<T, unknown>
): boolean {
  if (typeof editable === 'function') {
    return editable(cell); // Call the function with the cell context
  }

  return !!editable; // Return the boolean directly
}
